import {useTranslation} from "react-i18next";
import {useEffect, useRef, useState} from "react";
import {Button, Form, Input, Switch, Alert, Table, Space, Spin, Tag, Typography, Row, Col, Card} from "antd";
import ApiService from "../../core/service/api-service";
import AeTitleSelectBox from "./AeTitleSelectBox";
import {SearchOutlined} from "@ant-design/icons";

const {TextArea} = Input;
const {Text} = Typography;

export default function AutoExportRuleCreateEdit({ruleId, onSaved}) {
    const {t} = useTranslation();
    const formRef = useRef();
    const [testResult, setTestResult] = useState(null);
    const [testLoading, setTestLoading] = useState(false);
    const [filterMetadata, setFilterMetadata] = useState(null);

    const loadFilterMetadata = async () => {
        const response = await ApiService.AutoExport.GetFilterMetadata();
        if (response.success) {
            setFilterMetadata(response.data);
        }
    }

    const getRule = async () => {
        if(ruleId) {
            const response = await ApiService.AutoExport.GetRule(ruleId);
            if(response.success && response.data) {
                formRef.current.setFieldsValue({
                    name: response.data.name,
                    description: response.data.description,
                    sqlQuery: response.data.sqlQuery,
                    targetAeTitle: response.data.targetAeTitle,
                    isEnabled: response.data.isEnabled,
                });
            }
        }
    }

    const save = async (form) => {
        let response;
        if(ruleId) {
            response = await ApiService.AutoExport.UpdateRule(ruleId, form);
        } else {
            response = await ApiService.AutoExport.CreateRule(form);
        }

        if(response.success) {
            if(onSaved) {
                onSaved();
            }
        }
    }

    const testQuery = async () => {
        const sqlQuery = formRef.current.getFieldValue('sqlQuery');
        if (!sqlQuery) {
            setTestResult({ isValid: false, error: t('Please enter SQL query') });
            return;
        }
        
        setTestLoading(true);
        setTestResult(null);
        
        const response = await ApiService.AutoExport.TestQuery(sqlQuery);
        if (response.success) {
            setTestResult(response.data);
        } else {
            setTestResult({ isValid: false, error: response.message || t('Test failed') });
        }
        
        setTestLoading(false);
    }

    useEffect(() => {
        getRule();
        loadFilterMetadata();
    }, [ruleId]);

    const [filterSearch, setFilterSearch] = useState({});

    const renderFilterTable = (key, title, columnName, items, color) => {
        const searchValue = filterSearch[key] || '';
        const filteredItems = items?.filter(item => 
            item.name.toLowerCase().includes(searchValue.toLowerCase()) ||
            item.id.toString().includes(searchValue)
        );
        
        return (
            <Card 
                size="small" 
                title={`${title} (${columnName})`} 
                style={{marginBottom: 8}}
                extra={
                    <Input
                        size="small"
                        placeholder={t('Search')}
                        prefix={<SearchOutlined />}
                        value={searchValue}
                        onChange={(e) => setFilterSearch(prev => ({...prev, [key]: e.target.value}))}
                        style={{width: 150}}
                        allowClear
                    />
                }
            >
                <Space wrap size={[4, 4]}>
                    {filteredItems?.map(item => (
                        <Tag key={item.id} color={color}>
                            <Text copyable={{text: item.id.toString()}} style={{fontSize: 12}}>
                                {item.name} <Text type="secondary">({item.id})</Text>
                            </Text>
                        </Tag>
                    ))}
                </Space>
            </Card>
        );
    };

    const testColumns = [
        { title: 'Study ID', dataIndex: 'studyId', key: 'studyId' },
        { title: 'Study Instance UID', dataIndex: 'studyInstanceUid', key: 'studyInstanceUid', ellipsis: true },
        { title: 'PACS System ID', dataIndex: 'pacsSystemId', key: 'pacsSystemId' },
    ];

    return (
        <Row gutter={16}>
            <Col span={12}>
                <Form
                    layout={'vertical'}
                    ref={formRef}
                    onFinish={save}
                    initialValues={{
                        isEnabled: true
                    }}
                >
                    <Form.Item
                        name={'name'}
                        label={t('Rule Name')}
                        rules={[
                            {required: true, message: t('Please enter rule name')}
                        ]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name={'description'}
                        label={t('Description')}
                    >
                        <TextArea rows={2} />
                    </Form.Item>

                    <Form.Item
                        name={'sqlQuery'}
                        label={t('SQL Query')}
                        rules={[
                            {required: true, message: t('Please enter SQL query')}
                        ]}
                        extra={t('SQL query must return: study_id, study_instance_uid, pacs_system_id')}
                    >
                        <TextArea 
                            rows={6} 
                            placeholder={'SELECT s.id as study_id, s.study_instance_uid, s.pacs_system_id\nFROM study s\nWHERE s.is_deleted = false AND s.modality_id = 1'}
                            style={{fontFamily: 'monospace'}}
                        />
                    </Form.Item>

                    <div style={{marginBottom: 16}}>
                        <Button onClick={testQuery} loading={testLoading}>
                            {t('Test Query')}
                        </Button>
                    </div>

                    {testLoading && <Spin style={{marginBottom: 16}} />}

                    {testResult && (
                        <div style={{marginBottom: 16}}>
                            {testResult.isValid ? (
                                <>
                                    <Alert 
                                        type="success" 
                                        message={`${t('Query is valid')} - ${t('Total matching studies')}: ${testResult.totalCount}`}
                                        style={{marginBottom: 8}}
                                    />
                                    {testResult.sampleRows && testResult.sampleRows.length > 0 && (
                                        <Table 
                                            dataSource={testResult.sampleRows}
                                            columns={testColumns}
                                            rowKey="studyId"
                                            size="small"
                                            pagination={false}
                                            scroll={{x: true}}
                                        />
                                    )}
                                </>
                            ) : (
                                <Alert 
                                    type="error" 
                                    message={testResult.error}
                                />
                            )}
                        </div>
                    )}

                    <Form.Item
                        label={t('Target AE Title')}
                        name={'targetAeTitle'}
                        rules={[
                            {required: true, message: t('Please select target AE Title')}
                        ]}
                    >
                        <AeTitleSelectBox />
                    </Form.Item>

                    <Form.Item
                        name={'isEnabled'}
                        label={t('Enabled')}
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>

                    <Form.Item>
                        <Button type={'primary'} htmlType="submit">
                            {t('Save')}
                        </Button>
                    </Form.Item>
                </Form>
            </Col>
            <Col span={12}>
                {filterMetadata && (
                    <div style={{maxHeight: '70vh', overflowY: 'auto'}}>
                        {renderFilterTable('modalities', t('Modalities'), 'modality_id', filterMetadata.modalities, 'blue')}
                        {renderFilterTable('pacsSystems', t('PACS Systems'), 'pacs_system_id', filterMetadata.pacsSystems, 'green')}
                        {renderFilterTable('requestTypes', t('Request Types'), 'request_type_id', filterMetadata.requestTypes, 'purple')}
                        {renderFilterTable('studyTypes', t('Study Types'), 'study_type', filterMetadata.studyTypes, 'orange')}
                        {renderFilterTable('studyStatuses', t('Study Statuses'), 'study_status', filterMetadata.studyStatuses, 'cyan')}
                    </div>
                )}
            </Col>
        </Row>
    )
}
